import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Loader, LoaderModeType } from 'components/Loader';
import { PAGES } from 'constants/pages';
import { type GetTicketsQuery, useGetTicketsQuery } from 'data/__generated__';
import { formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';

import { StatePill, type StateTone } from './StopStatus';

// Odoo teams Appro (19) and Tech (20), the ones working on site: `TOTOPS_OPERABLE_TICKET_TEAM_IDS` on the API.
const SITE_TICKET_TEAM_IDS = [19, 20];
// « En attente client »: a folded stage, so closed for Odoo, yet the ticket is still pending on the site.
const WAITING_CUSTOMER_STAGE_ID = 19;
// Nothing to do on site: « To Reassign » (15), « Pending Validation » (16) and « En attente client ».
const NOT_TO_DO_STAGE_IDS = [15, 16, WAITING_CUSTOMER_STAGE_ID];
// The field acts from « Assigned to Field » (13): before it, TotOps hides « New » and « Assigned to Manager » only.
const FIELD_STAGE_IDS = [13, 14];
const RESOLVED_STAGE_ID = 17;

type SiteTicket = GetTicketsQuery['tickets'][number];

const isToDo = ({ stage }: SiteTicket): boolean => !NOT_TO_DO_STAGE_IDS.includes(stage?.id ?? -1);

const stageTone = (ticket: SiteTicket): StateTone =>
    ticket.stage?.id === RESOLVED_STAGE_ID
        ? 'success'
        : !isToDo(ticket)
          ? 'neutral'
          : FIELD_STAGE_IDS.includes(ticket.stage?.id ?? -1)
            ? 'info'
            : 'warning';

/**
 * The tickets resolved on the task's `day` (`YYYY-MM-DD`), to check the work done, then the site's pending ones,
 * split between what the stop is for and what waits on someone else.
 */
export const TaskTickets = ({ siteId, day }: { siteId: string; day: string }) => {
    const { data, error } = useGetTicketsQuery({
        variables: { filters: { siteId, teamIds: SITE_TICKET_TEAM_IDS } },
        fetchPolicy: 'cache-and-network',
    });

    if (!data) {
        return error ? (
            <Meta>Tickets indisponibles : {error.message}</Meta>
        ) : (
            <Loader size="20px" mode={LoaderModeType.Spin} />
        );
    }

    const tickets = data.tickets.filter(({ isClosed, stage }) => !isClosed || stage?.id === WAITING_CUSTOMER_STAGE_ID);
    const resolvedTickets = data.tickets.filter(
        ({ stage, closeDate }) =>
            stage?.id === RESOLVED_STAGE_ID &&
            closeDate != null &&
            formatDateAsAnniversary({ dateTime: closeDate }) === day,
    );
    if (!tickets.length && !resolvedTickets.length) return <Meta>Aucun ticket en cours sur ce site</Meta>;

    return (
        <Sections>
            <TicketSection
                title={`Résolus le ${formatDateForDisplay(day)}`}
                tickets={resolvedTickets}
                dataTest="task-tickets-resolved"
            />
            <TicketSection
                title="À faire"
                tickets={tickets.filter(isToDo)}
                showCreateDate
                dataTest="task-tickets-to-do"
            />
            <TicketSection
                title="Pas à faire"
                hint="validation, réaffectation ou attente client"
                tickets={tickets.filter((ticket) => !isToDo(ticket))}
                dataTest="task-tickets-not-to-do"
            />
        </Sections>
    );
};

const TicketSection = ({
    title,
    hint,
    tickets,
    showCreateDate,
    dataTest,
}: {
    title: string;
    hint?: string;
    tickets: SiteTicket[];
    showCreateDate?: boolean;
    dataTest: string;
}) => (
    <section data-test={dataTest}>
        <SectionTitle>
            {title}
            <Count>{tickets.length}</Count>
            {hint && <Hint>{hint}</Hint>}
        </SectionTitle>
        {tickets.length ? (
            <Tickets>
                {tickets.map((ticket) => (
                    <Row key={ticket._id}>
                        <RowMain>
                            <TicketLink
                                to={PAGES.ticketDetails.url.replace(':ticketId', ticket._id)}
                                target="_blank"
                                data-test="task-ticket-link"
                            >
                                {[ticket.odooRef ?? ticket.odooId, ticket.name, ticket.team?.name]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </TicketLink>
                            <Meta>
                                {[
                                    ticket.microstoreColumn?.name ?? ticket.machine?.name,
                                    showCreateDate &&
                                        ticket.odooCreateDate != null &&
                                        `Créé le ${formatDateForDisplay(ticket.odooCreateDate)}`,
                                    ticket.operator && `Pris en charge par ${ticket.operator.fullname}`,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Meta>
                        </RowMain>
                        {ticket.stage && <StatePill $tone={stageTone(ticket)}>{ticket.stage.name}</StatePill>}
                    </Row>
                ))}
            </Tickets>
        ) : (
            <Meta>Aucun ticket</Meta>
        )}
    </section>
);

const Sections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 6px;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding-bottom: 6px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.textColor};
`;

const Count = styled.span`
    min-width: 20px;
    padding: 1px 6px;
    border-radius: 999px;
    background: ${({ theme }) => theme.lightBorderColor};
    font-size: 11px;
    text-align: center;
`;

const Hint = styled.span`
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: ${({ theme }) => theme.infoTextColor};
`;

const Tickets = styled.ul`
    display: flex;
    flex-direction: column;
`;

const Row = styled.li`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};

    &:last-child {
        border-bottom: none;
    }
`;

const RowMain = styled.div`
    min-width: 0;
`;

const TicketLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryColor};

    &:hover {
        text-decoration: underline;
    }
`;

const Meta = styled.span`
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;
